/**
 * `<input type="datetime-local">` values, converted honestly.
 *
 * 🔴 **A datetime-local value has NO timezone, and is NOT UTC.** The control
 * yields `'2026-08-11T09:45'` — the wall-clock the person is looking at. Post
 * that string as-is and the server has no way to know it means 09:45 in
 * Swindon rather than 09:45 UTC. Every backend in this estate guessed UTC, so
 * everything scheduled through a form ran an hour late for the whole of BST.
 *
 * ⚠️ **Seasonal, which is why nobody caught it.** The UK is UTC+0 in winter,
 * so the naive string and the correct answer are identical from late October
 * to late March. It breaks every spring and fixes itself every autumn.
 *
 * ⚠️ **`new Date('2026-08-11T09:45')` is not the bug** — a date-time string
 * with no offset is parsed as LOCAL time by the spec, which is exactly what
 * we want. But `new Date('2026-08-11')`, a date-ONLY string, is parsed as
 * **UTC**. That inconsistency is in the language, it bites at midnight
 * boundaries, and it is why `localInputToISO` refuses bare dates rather than
 * quietly returning a value that is right in London and wrong in Prague.
 */

/**
 * A datetime-local field value -> an offset-aware ISO string for the API.
 *
 *   '2026-08-11T09:45'  (viewer in BST)  -> '2026-08-11T08:45:00.000Z'
 *   '2026-08-11T09:45'  (viewer in CET)  -> '2026-08-11T07:45:00.000Z'
 *
 * The browser is the only participant that knows the reader's offset, which
 * is why this belongs here and not on the server. Staff in Prague and New
 * York are not on London time, so a server-side "assume UK" is a fallback,
 * never the answer.
 *
 * Returns null for empty input, so `value || null` call sites keep working.
 */
export function localInputToISO(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  // Refuse a bare date: see the header — the language parses it as UTC, and
  // silently accepting it here would put midnight on the wrong day.
  if (!s.includes('T')) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * An ISO string from the API -> the `YYYY-MM-DDTHH:mm` a datetime-local needs.
 *
 * ⚠️ Assigning a raw ISO string to a datetime-local leaves the field
 * **silently blank** — the input rejects a value it cannot parse rather than
 * reporting anything. On an edit form that reads as "this had no date set",
 * and saving then clears it. Hit at least three times in this estate (event
 * edit, survey edit, announcement schedule).
 *
 * Uses local getters deliberately: the field shows the reader THEIR time.
 */
export function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
       + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The reader's IANA zone (`'Europe/London'`), or '' if unavailable.
 * Useful for telling the server what the browser believes, and for showing
 * "10:45 (Europe/London)" where an audience spans countries.
 */
export function viewerTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}
